import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_86ba7c87-2c5f-4809-b4ca-291f6e117aa2/artifacts/wjmihcrt_black%20rento%20logo.png";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password-with-token', {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successful!');
    } catch (error) {
      console.error('Reset password error:', error);
      const message = error.response?.data?.detail || 'Failed to reset password. Link may have expired.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#2a2a2a]">
        <div className="w-full max-w-md text-center space-y-6">
          <img src={LOGO_URL} alt="rentOcart.com" className="h-16 object-contain mx-auto mb-6" />
          <div className="bg-red-900/30 border border-red-700 p-6 rounded-xl">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2 text-red-400">Reset Failed</h2>
            <p className="text-gray-300 mb-4">{error}</p>
          </div>
          <Link to="/forgot-password">
            <Button className="w-full h-12 bg-gradient-to-r from-cyan-500 to-green-500">
              Request New Reset Link
            </Button>
          </Link>
          <Link to="/login" className="text-cyan-400 hover:text-cyan-300 hover:underline inline-flex items-center">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#2a2a2a]">
        <div className="w-full max-w-md text-center space-y-6">
          <img src={LOGO_URL} alt="rentOcart.com" className="h-16 object-contain mx-auto mb-6" />
          <div className="bg-green-900/30 border border-green-700 p-6 rounded-xl">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2 text-white">Password Reset!</h2>
            <p className="text-gray-300 mb-4">
              Your password has been successfully reset. You can now login with your new password.
            </p>
          </div>
          <Button onClick={() => navigate('/login')} className="w-full h-12 bg-gradient-to-r from-cyan-500 to-green-500">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#2a2a2a]">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <img src={LOGO_URL} alt="rentOcart.com" className="h-16 object-contain mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-2 text-white">Reset Your Password</h2>
          <p className="text-gray-400">
            Enter your new password below.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="password" className="text-gray-300">New Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input
                id="password"
                type="password"
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="h-14 text-lg pl-11 bg-[#1a1a1a] border-gray-700 text-white placeholder:text-gray-500 focus:border-cyan-500"
                data-testid="new-password-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="confirmPassword" className="text-gray-300">Confirm Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="h-14 text-lg pl-11 bg-[#1a1a1a] border-gray-700 text-white placeholder:text-gray-500 focus:border-cyan-500"
                data-testid="confirm-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-14 text-lg font-bold bg-gradient-to-r from-cyan-500 via-green-500 to-orange-500 hover:from-cyan-600 hover:via-green-600 hover:to-orange-600"
            data-testid="reset-password-btn"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </form>

        <div className="text-center">
          <Link to="/login" className="text-cyan-400 hover:text-cyan-300 hover:underline inline-flex items-center">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
